// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Switch } from "@/components/ui/switch";

interface ChartHeadersProps {
  showOutgoingLine: boolean;
  setShowOutgoingLine: (show: boolean) => void;
  customTimeRange: { start: Date; end: Date } | null;
  setCustomTimeRange: (range: { start: Date; end: Date } | null) => void;
  headerHeight?: number; // Dynamic header height
}

export function ChartHeaders({
  showOutgoingLine,
  setShowOutgoingLine,
  customTimeRange,
  setCustomTimeRange,
  headerHeight = 40, // Default height if not provided
}: ChartHeadersProps) {
  // Calculate padding based on header height
  // Minimum padding of 4px, max of 16px, scaled based on header height
  const paddingTop = Math.max(4, Math.min(12, Math.floor(headerHeight * 0.25)));
  const marginBottom = Math.max(0, Math.min(8, Math.floor(headerHeight * 0.1)));

  return (
    <div
      className="px-6"
      style={{
        paddingTop: `${paddingTop}px`,
        height: `${headerHeight}px`,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        className="flex items-center w-full"
        style={{ marginBottom: `${marginBottom}px` }}
      >
        <h2 className="text-foreground mr-2">Navigation Minimap</h2>

        {/* Controls moved here, right after the title */}
        <div className="flex items-center gap-4 ml-4 pl-4">
          {/* Show Bids Control */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Show bids</span>
              <InfoTooltip
                trigger={
                  <button className="text-muted-foreground hover:text-foreground transition-colors">
                    <InfoCircledIcon className="h-3 w-3" />
                  </button>
                }
                side="top"
                className="w-[250px]"
              >
                <div className="text-sm">
                  <h4 className="font-medium mb-1">Outgoing Touchpoints</h4>
                  <p className="text-muted-foreground">
                    Bids represent outgoing activities from your team to this
                    account. Enabling this shows a secondary line tracking your
                    outreach efforts.
                  </p>
                </div>
              </InfoTooltip>
            </div>
            <Switch
              checked={showOutgoingLine}
              onCheckedChange={setShowOutgoingLine}
              aria-label="Toggle outgoing activity line"
              className="scale-75 data-[state=checked]:bg-primary/80"
            />
          </div>

          {/* Reset Zoom Control - Only shown when custom time range is active */}
          {customTimeRange && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCustomTimeRange(null)}
                className="flex items-center gap-1 h-6 px-2 py-0 text-xs text-muted-foreground hover:text-foreground"
              >
                <span>Reset zoom</span>
                <RotateCcw className="h-3 w-3 ml-0.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Info icon moved to the right edge */}
        <div className="ml-auto">
          <InfoTooltip
            trigger={
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <InfoCircledIcon className="h-4 w-4" />
              </button>
            }
            side="right"
            className="w-[300px]"
          >
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium mb-1">Velocity Sparkline</h4>
                <p className="text-sm text-muted-foreground">
                  Indicates the momentum of engagement coming from the account
                  over time. Higher peaks indicate more incoming engagement.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">
                  Opportunity Timeline
                </h4>
                <p className="text-sm text-muted-foreground">
                  Colored bars represent opportunities:
                </p>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                  <li>🟢 Green - Won opportunities</li>
                  <li>🔴 Red - Lost opportunities</li>
                  <li>🔵 Blue - Active opportunities</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-1">
                  Color shades indicate stage progression - lighter shades
                  represent earlier stages, and darker shades represent later
                  stages in the opportunity lifecycle.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">
                  New Person Engagements
                </h4>
                <p className="text-sm text-muted-foreground">
                  Circles indicate first actions from a new person in the
                  account. Hover over each circle to see person details.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">
                  Filtered Activity Markers
                </h4>
                <p className="text-sm text-muted-foreground">
                  Small dots along the timeline show when filtered activities
                  occurred. These appear when you use the filters below the
                  chart (people, channels, teams, or search).
                </p>
              </div>
            </div>
          </InfoTooltip>
        </div>
      </div>
    </div>
  );
}
